import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import { errorTracking } from "./lib/errorTracking";

// Initialize Sentry with comprehensive logging
try {
Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || "https://f8b04f6df3abbf35624fa6b201a73d21@o4510248132083712.ingest.us.sentry.io/4510376742223872",
    debug: import.meta.env.DEV,
    enabled: true, // Explicitly enable Sentry
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0,
    
    // Enhanced for React Query
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.supabase\.co/,
    ],
    
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Release tracking
    release: import.meta.env.VITE_SENTRY_RELEASE || `radius-frontend@${new Date().toISOString()}`,
    environment: import.meta.env.MODE,
    // Setting this option to true will send default PII data to Sentry.
    sendDefaultPii: true,
    // Transport options
    transport: undefined, // Use default transport
    // Ensure errors are captured
    beforeSend(event, hint) {
      const originalError = hint.originalException;

      // Detect EdgeFunctionError and enhance event for better grouping
      if (originalError && typeof originalError === 'object' && (originalError as Error).name === 'EdgeFunctionError') {
        const efError = originalError as {
          functionName: string;
          requestUrl: string;
          httpMethod: string;
          httpStatus?: number;
          durationMs: number;
          callerOperation?: string;
          message: string;
        };
        
        // Improve issue title for better grouping
        if (event.exception?.values?.[0]) {
          event.exception.values[0].type = 'EdgeFunctionError';
          event.exception.values[0].value = `${efError.functionName}: ${efError.message.replace(/^EdgeFunctionError\([^)]+\):\s*/, '')}`;
        }
        
        // Add context if not already present
        event.contexts = event.contexts || {};
        event.contexts.edge_function = {
          function_name: efError.functionName,
          request_url: efError.requestUrl,
          http_method: efError.httpMethod,
          http_status: efError.httpStatus,
          duration_ms: efError.durationMs,
          caller_operation: efError.callerOperation,
        };

        // Ensure tags are set
        event.tags = event.tags || {};
        event.tags.error_source = 'edge_function';
        event.tags.edge_function_name = efError.functionName;
        if (efError.httpStatus) {
          event.tags.http_status = String(efError.httpStatus);
        }
      }
      
      // Detect FunctionsFetchError from Supabase SDK and mark it
      if (originalError && typeof originalError === 'object') {
        const errName = (originalError as Error).name;
        const errMessage = (originalError as Error).message || '';
        
        if (errName === 'FunctionsFetchError' || errMessage.includes('Edge Function')) {
          event.tags = event.tags || {};
          event.tags.error_source = event.tags.error_source || 'edge_function_sdk';
        }
      }
      
      // Add React Query breadcrumb if available
      if (originalError && typeof originalError === 'object') {
        const extra = (originalError as { extra?: { queryKey?: unknown; operation?: string } }).extra;
        if (extra?.queryKey) {
          event.contexts = event.contexts || {};
          event.contexts.react_query = {
            query_key: extra.queryKey,
            operation: extra.operation,
          };
        }
      }
      
      return event;
    },
  });
  
  if (import.meta.env.DEV) {
    console.log('[Sentry] Initialized successfully');
    console.log('[Sentry] Environment:', import.meta.env.MODE);
    console.log('[Sentry] Release:', import.meta.env.VITE_SENTRY_RELEASE || 'not set');
  }
  
  // Expose test error function in development only
  if (import.meta.env.DEV) {
    (window as any).testSentryError = () => {
      console.log('[Sentry] Testing error capture...');
      Sentry.captureException(new Error('Test error from Sentry'));
      throw new Error('Test error - this should appear in Sentry');
    };
    console.log('[Sentry] Test function available: window.testSentryError()');
  }
} catch (error) {
  console.error('[Sentry] Failed to initialize:', error);
}

// Global error handlers
window.addEventListener('error', (event) => {
  errorTracking.captureError(event.error, { 
    url: event.filename, 
    line: event.lineno,
    column: event.colno 
  });
});

window.addEventListener('unhandledrejection', (event) => {
  errorTracking.captureUnhandledRejection(event);
});

createRoot(document.getElementById("root")!).render(<App />);
